import { useState, useEffect } from "react";

const calculateTimeLeft = (expirationDate: Date | null | undefined): string => {
  if (!expirationDate) return "";

  const difference = +expirationDate - +new Date();
  let timeLeftString = "";

  if (difference <= 0) {
    return "Expired";
  }

  const minutes = Math.floor((difference / (1000 * 60)) % 60);
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));

  if (days > 0) {
    timeLeftString = `${days}d`;
    if (hours > 0) timeLeftString += ` ${hours}h`;
  } else if (hours > 0) {
    timeLeftString = `${hours}h`;
    if (minutes > 0) timeLeftString += ` ${minutes}m`;
  } else if (minutes > 0) {
    timeLeftString = `${minutes}m`;
  } else {
    timeLeftString = "< 1m";
  }
  return timeLeftString;
};

interface UseExpirationTimerOptions {
  expirationDate: Date | null | undefined;
  onExpired?: () => void;
}

export const useExpirationTimer = ({
  expirationDate,
  onExpired,
}: UseExpirationTimerOptions): string => {
  const [timeLeft, setTimeLeft] = useState<string>(() =>
    calculateTimeLeft(expirationDate),
  );

  useEffect(() => {
    if (!expirationDate) {
      setTimeLeft("");
      return;
    }

    if (+expirationDate - +new Date() <= 0) {
      setTimeLeft("Expired");
      if (onExpired) onExpired();
      return;
    }

    // Initial calculation
    setTimeLeft(calculateTimeLeft(expirationDate));

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expirationDate);
      setTimeLeft(newTimeLeft);

      if (newTimeLeft === "Expired") {
        if (onExpired) onExpired();
        clearInterval(timer);
      }
    }, 30000); // Update every 30 seconds, adjust as needed

    return () => clearInterval(timer);
  }, [expirationDate, onExpired]);

  return timeLeft;
};
